import type {
  DownloadInlineBlobInput,
  DownloadInlinePdfInput,
  DownloadOutcome,
  PdfDownloadService,
} from "./PdfDownloadService.js";

/**
 * Phase 13h — production browser adapter for `PdfDownloadService`.
 *
 * The adapter is the **only** module under `apps/web/src/` that touches
 * `window.open`, `URL.createObjectURL`, `document.createElement`, or
 * `Blob` directly. The Phase 13.5 native wrap swaps this for a
 * Capacitor-backed adapter (Filesystem.write + Share.share); the
 * consumer code stays unchanged.
 *
 * Construction takes a `window` / `document` pair so unit tests with
 * happy-dom can inject minimal stubs without colliding with the
 * production globals. Production callers wire
 * `new BrowserPdfDownloadService({ window, document })` from the App
 * root.
 */

export type BrowserWindowLike = {
  open(url: string, target?: string): unknown;
  URL: {
    createObjectURL(blob: Blob): string;
    revokeObjectURL(url: string): void;
  };
  atob(input: string): string;
};

export type BrowserDocumentLike = {
  createElement(tagName: "a"): HTMLAnchorElementLike;
  body: { appendChild(node: unknown): void; removeChild(node: unknown): void };
};

export type HTMLAnchorElementLike = {
  href: string;
  download: string;
  rel: string;
  click(): void;
};

export type BrowserPdfDownloadOptions = {
  readonly window: BrowserWindowLike;
  readonly document: BrowserDocumentLike;
  /**
   * Milliseconds to retain a minted Blob URL before revoking. Default
   * 30s — the browser typically issues the file dialog inside the
   * first second of the synthetic click; 30s is comfortable headroom.
   */
  readonly retainBlobUrlMs?: number;
  /**
   * Inject a deferred-revoke scheduler. Production wires
   * `setTimeout`; tests can pass a synchronous scheduler so the
   * revoke runs inline.
   */
  readonly schedule?: (cb: () => void, ms: number) => void;
};

export class BrowserPdfDownloadService implements PdfDownloadService {
  private readonly window: BrowserWindowLike;
  private readonly document: BrowserDocumentLike;
  private readonly retainBlobUrlMs: number;
  private readonly schedule: (cb: () => void, ms: number) => void;

  constructor(options: BrowserPdfDownloadOptions) {
    this.window = options.window;
    this.document = options.document;
    this.retainBlobUrlMs = options.retainBlobUrlMs ?? 30_000;
    this.schedule =
      options.schedule ??
      ((cb, ms) => {
        // Cast through `as unknown as` because Node's `setTimeout`
        // returns a `Timeout`, not the DOM's `number`.
        (globalThis as { setTimeout: (cb: () => void, ms: number) => unknown })
          .setTimeout(cb, ms);
      });
  }

  async openPresignedUrl(url: string): Promise<DownloadOutcome> {
    const opened = this.window.open(url, "_blank");
    if (opened === null || opened === undefined) {
      return {
        kind: "failure",
        message:
          "We couldn't open the PDF in a new tab. Check your pop-up blocker and try again.",
      };
    }
    return { kind: "ok" };
  }

  async downloadInlinePdf(
    input: DownloadInlinePdfInput,
  ): Promise<DownloadOutcome> {
    return this.downloadInlineBlob({
      filename: input.filename,
      contentType: "application/pdf",
      payload: { kind: "base64", base64: input.pdfBase64 },
    });
  }

  async downloadInlineBlob(
    input: DownloadInlineBlobInput,
  ): Promise<DownloadOutcome> {
    let bytes: Uint8Array;
    try {
      bytes =
        input.payload.kind === "base64"
          ? decodeBase64(input.payload.base64, this.window.atob)
          : encodeUtf8(input.payload.text);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Decode failed";
      return { kind: "failure", message };
    }
    const blob = new Blob([bytes as unknown as ArrayBuffer], {
      type: input.contentType,
    });
    const objectUrl = this.window.URL.createObjectURL(blob);
    try {
      const anchor = this.document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = input.filename;
      anchor.rel = "noopener";
      this.document.body.appendChild(anchor);
      anchor.click();
      this.document.body.removeChild(anchor);
    } finally {
      this.schedule(() => {
        this.window.URL.revokeObjectURL(objectUrl);
      }, this.retainBlobUrlMs);
    }
    return { kind: "ok" };
  }
}

const decodeBase64 = (
  b64: string,
  atob: (input: string) => string,
): Uint8Array => {
  const binary = atob(b64);
  const buf = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    buf[i] = binary.charCodeAt(i);
  }
  return buf;
};

/**
 * Phase 14a — UTF-8 encode a text payload for the inline-blob
 * download path. Uses `TextEncoder` (a Web Platform feature available
 * in every browser + Node 11+); the seam still treats this as
 * structurally a "browser global" the adapter owns.
 */
const encodeUtf8 = (text: string): Uint8Array => {
  // `TextEncoder` is a global on web + Node 22; treat it as the
  // adapter's encoding primitive (same shape as `atob`).
  const encoder = new (
    globalThis as { TextEncoder: new () => { encode(s: string): Uint8Array } }
  ).TextEncoder();
  return encoder.encode(text);
};
